// src/pages/AdminPanel.jsx
import { useState, useEffect, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const CLOUD_NAME = "dpvpc2ykb";
const UPLOAD_PRESET = "pultks";

/* ── Global ─────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Figtree:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f5f6fa; font-family: 'Figtree', sans-serif; }
`;

const C = {
  bg: "#f5f6fa",
  white: "#ffffff",
  blue: "#1a56ff",
  blueSoft: "#eef2ff",
  blueHover: "#1040d4",
  text: "#0d0d0d",
  sub: "#6b7280",
  border: "#e8eaed",
  danger: "#ef4444",
  dangerSoft: "#fef2f2",
};

/* ── Layout ─────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  max-width: 1300px;
  margin: auto;
  background: ${C.bg};
  margin-top: 7em;
  padding: 40px 48px;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 36px;
`;

const PageTitle = styled.h1`
  font-family: "Syne", sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${C.text};
  letter-spacing: -0.02em;

  span {
    color: ${C.blue};
  }
`;

const LogoutBtn = styled.button`
  font-family: "Figtree", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${C.sub};
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 10px;
  padding: 8px 18px;
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    border-color: ${C.danger};
    color: ${C.danger};
    background: ${C.dangerSoft};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Card shell ─────────────────────────────────────────────── */
const Card = styled.div`
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 20px;
  padding: 28px;
`;

const CardTitle = styled.h2`
  font-family: "Syne", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${C.text};
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${C.blue};
    flex-shrink: 0;
  }
`;

/* ── Form fields ────────────────────────────────────────────── */
const Field = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${C.sub};
    margin-bottom: 7px;
  }

  input {
    width: 100%;
    background: #fafafa;
    border: 1.5px solid ${C.border};
    border-radius: 12px;
    padding: 11px 14px;
    font-family: "Figtree", sans-serif;
    font-size: 14px;
    color: ${C.text};
    outline: none;
    transition:
      border-color 0.18s,
      box-shadow 0.18s,
      background 0.18s;

    &:focus {
      border-color: ${C.blue};
      background: #fff;
      box-shadow: 0 0 0 3px #1a56ff18;
    }
    &::placeholder {
      color: #c0c4cc;
    }
  }
`;

const ImageUploadArea = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 120px;
  border: 1.5px dashed ${({ $hasImage }) => ($hasImage ? C.blue : C.border)};
  border-radius: 12px;
  cursor: pointer;
  background: ${({ $hasImage }) => ($hasImage ? C.blueSoft : "#fafafa")};
  overflow: hidden;
  position: relative;
  transition:
    border-color 0.18s,
    background 0.18s;

  &:hover {
    border-color: ${C.blue};
    background: ${C.blueSoft};
  }

  input {
    display: none;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.55;
  }

  .label-text {
    position: relative;
    z-index: 1;
    font-size: 12px;
    font-weight: 500;
    color: ${({ $hasImage }) => ($hasImage ? C.blue : C.sub)};
    text-align: center;
    line-height: 1.6;
  }
`;

const ErrorMsg = styled(motion.p)`
  font-size: 12px;
  font-weight: 500;
  color: ${C.danger};
  background: ${C.dangerSoft};
  border: 1.5px solid #fecaca;
  border-radius: 10px;
  padding: 9px 12px;
  margin-bottom: 14px;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 7px;
`;

const SubmitBtn = styled(motion.button)`
  width: 100%;
  margin-top: 6px;
  padding: 13px;
  background: ${({ $isEdit }) => ($isEdit ? "#0ea5e9" : C.blue)};
  border: none;
  border-radius: 12px;
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: background 0.18s;

  &:hover {
    background: ${({ $isEdit }) => ($isEdit ? "#0284c7" : C.blueHover)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelBtn = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 11px;
  background: transparent;
  border: 1.5px solid ${C.border};
  border-radius: 12px;
  font-family: "Figtree", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${C.sub};
  cursor: pointer;
  transition: all 0.18s;

  &:hover {
    border-color: ${C.danger};
    color: ${C.danger};
    background: ${C.dangerSoft};
  }
`;

/* ── Products panel ─────────────────────────────────────────── */
const PanelTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;

const CountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${C.blue};
  background: ${C.blueSoft};
  border-radius: 20px;
  padding: 4px 12px;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${C.sub};
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: #fafafa;
  border: 1.5px solid ${C.border};
  border-radius: 10px;
  padding: 9px 14px 9px 36px;
  font-family: "Figtree", sans-serif;
  font-size: 13px;
  color: ${C.text};
  outline: none;
  transition:
    border-color 0.18s,
    box-shadow 0.18s;

  &:focus {
    border-color: ${C.blue};
    box-shadow: 0 0 0 3px #1a56ff18;
    background: #fff;
  }
  &::placeholder {
    color: #c0c4cc;
  }
`;

/* ── Product rows ───────────────────────────────────────────── */
const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProductRow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1.5px solid ${({ $editing }) => ($editing ? C.blue : C.border)};
  border-radius: 14px;
  background: ${({ $editing }) => ($editing ? C.blueSoft : "#fafafa")};
  transition:
    border-color 0.18s,
    background 0.18s,
    box-shadow 0.18s;

  &:hover {
    border-color: ${C.blue};
    background: ${C.blueSoft};
    box-shadow: 0 2px 12px #1a56ff0d;
  }
`;

const ProductThumb = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  background: ${C.border};
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;

  .brand {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${C.blue};
    margin-bottom: 2px;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: ${C.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProductPrice = styled.span`
  font-family: "Syne", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${C.text};
  flex-shrink: 0;
`;

const RowActions = styled.div`
  display: flex;
  gap: 7px;
  flex-shrink: 0;
`;

const RowBtn = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-family: "Figtree", sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.16s;
  border: 1.5px solid ${({ $danger }) => ($danger ? "#fecaca" : C.border)};
  background: ${({ $danger }) => ($danger ? C.dangerSoft : C.white)};
  color: ${({ $danger }) => ($danger ? C.danger : C.sub)};

  &:hover {
    background: ${({ $danger }) => ($danger ? "#fee2e2" : C.blueSoft)};
    border-color: ${({ $danger }) => ($danger ? C.danger : C.blue)};
    color: ${({ $danger }) => ($danger ? C.danger : C.blue)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: ${C.sub};
  font-size: 14px;
  border: 1.5px dashed ${C.border};
  border-radius: 14px;
  background: #fafafa;
`;

/* ── Helpers ─────────────────────────────────────────────────── */
const EMPTY_FORM = { name: "", brand: "", price: "" };

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", UPLOAD_PRESET);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: "POST", body: formData },
  );
  if (!res.ok) throw new Error("Image upload failed");
  const data = await res.json();
  return data.secure_url;
}

/* ── Component ──────────────────────────────────────────────── */
export default function AdminPanel() {
  const navigate = useNavigate();
  const fileRef = useRef();

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "products"), (snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    return (
      !q ||
      p.name?.toLowerCase().includes(q) ||
      p.brand?.toLowerCase().includes(q)
    );
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview("");
    setEditingId(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!form.name || !form.brand || !form.price) {
      setError("All fields are required.");
      return;
    }
    if (!editingId && !imageFile) {
      setError("Please select a product image.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      let imageUrl = imagePreview;
      if (imageFile) imageUrl = await uploadToCloudinary(imageFile);

      const payload = {
        name: form.name.trim(),
        brand: form.brand.trim(),
        price: parseFloat(form.price),
        imageUrl,
      };

      if (editingId) {
        await updateDoc(doc(db, "products", editingId), payload);
      } else {
        await addDoc(collection(db, "products"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setForm({ name: product.name, brand: product.brand, price: product.price });
    setImagePreview(product.imageUrl);
    setImageFile(null);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await deleteDoc(doc(db, "products", id));
    if (editingId === id) resetForm();
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/x-admin");
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        {/* Header */}
        <PageHeader>
          <PageTitle>
            Admin <span>Panel</span>
          </PageTitle>
          <LogoutBtn onClick={handleLogout}>Sign out</LogoutBtn>
        </PageHeader>

        <Grid>
          {/* ── Form ── */}
          <Card style={{ position: "sticky", top: 24 }}>
            <CardTitle>{editingId ? "Edit Product" : "Add Product"}</CardTitle>

            <AnimatePresence>
              {error && (
                <ErrorMsg
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {error}
                </ErrorMsg>
              )}
            </AnimatePresence>

            <Field>
              <label>Image</label>
              <ImageUploadArea $hasImage={!!imagePreview}>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  onChange={handleImageChange}
                />
                {imagePreview && <img src={imagePreview} alt="preview" />}
                <div className="label-text">
                  {imagePreview ? "Click to change" : "Click to upload"}
                </div>
              </ImageUploadArea>
            </Field>

            <Field>
              <label>Product Name</label>
              <input
                placeholder="e.g. Samsung BN59-01315J"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>

            <Field>
              <label>Brand</label>
              <input
                placeholder="e.g. Samsung"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
              />
            </Field>

            <Field>
              <label>Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 29.99"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </Field>

            <SubmitBtn
              $isEdit={!!editingId}
              onClick={handleSubmit}
              disabled={loading}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <>
                  <Spinner />
                  {editingId ? "Saving…" : "Adding…"}
                </>
              ) : editingId ? (
                "Save Changes"
              ) : (
                "Add Product"
              )}
            </SubmitBtn>

            {editingId && <CancelBtn onClick={resetForm}>Cancel</CancelBtn>}
          </Card>

          {/* ── Products list ── */}
          <Card>
            <PanelTop>
              <CardTitle style={{ margin: 0 }}>Products</CardTitle>
              <CountBadge>{products.length} total</CountBadge>
              <SearchWrap>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <SearchInput
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchWrap>
            </PanelTop>

            <ProductList>
              <AnimatePresence>
                {filtered.length === 0 && (
                  <EmptyState key="empty">
                    {search
                      ? "No products match your search."
                      : "No products yet. Add your first one →"}
                  </EmptyState>
                )}
                {filtered.map((p) => (
                  <ProductRow
                    key={p.id}
                    $editing={editingId === p.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ProductThumb src={p.imageUrl} alt={p.name} />
                    <ProductInfo>
                      <div className="brand">{p.brand}</div>
                      <div className="name">{p.name}</div>
                    </ProductInfo>
                    <ProductPrice>${Number(p.price).toFixed(2)}</ProductPrice>
                    <RowActions>
                      <RowBtn onClick={() => handleEdit(p)}>Edit</RowBtn>
                      <RowBtn $danger onClick={() => handleDelete(p.id)}>
                        Delete
                      </RowBtn>
                    </RowActions>
                  </ProductRow>
                ))}
              </AnimatePresence>
            </ProductList>
          </Card>
        </Grid>
      </Page>
    </>
  );
}
